import { ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmCloseModal } from '@/shared/ui/';
import { useUploadModeSelection, type UploadMode } from './useUploadModeSelection';
import { useUrlUploadInstances } from './useUrlUploadInstances';
import { urlUploadDataStore } from './useUrlUploadDataStore';

interface UseUrlUploadModalDependencies {
  cleanState: () => void;
  handleAllFilesDeleted: () => void;
  isUrlUploadValid: Ref<boolean>;
  hasFiles: Ref<boolean>;
  onCancelModeChange?: () => void;
}

export default function useUrlUploadModal(dependencies: UseUrlUploadModalDependencies) {
  const {
    cleanState,
    handleAllFilesDeleted,
    isUrlUploadValid,
    hasFiles,
    onCancelModeChange,
  } = dependencies;

  const {
    uploadMode,
    shouldConfirmModeChange,
    setInitialMode,
    setPendingMode,
    confirmModeChange,
    cancelModeChange,
  } = useUploadModeSelection();

  const {
    urlUploadInstances,
    addUrlUploadInstance,
    removeUrlUploadInstance: removeInstance,
    handleUrlValidityChange: updateUrlValidity,
    resetInstances,
    areAllUrlsValid,
    hasValidUrls,
  } = useUrlUploadInstances();

  const handleUrlValidityChange = (index: number, isValid: boolean) => {
    updateUrlValidity(index, isValid);
    isUrlUploadValid.value = areAllUrlsValid.value;
  };

  const { open: openRemoveConfirmation, close: closeRemoveConfirmation } = useModal({
    component: ConfirmCloseModal,
    attrs: {
      // @ts-ignore
      clickToClose: false,
      escToClose: false,
      title: 'Tem certeza que deseja deletar o documento?',
      description: 'A ação não pode ser desfeita.',
      onClose() {
        closeRemoveConfirmation();
      },
      onConfirm() {
        closeRemoveConfirmation();
        if (pendingRemoveIndex.value !== null) {
          if (urlUploadInstances.value.length > 1) {
            removeInstance(pendingRemoveIndex.value);
          }
          pendingRemoveIndex.value = null;
        }
      },
    },
  });

  const pendingRemoveIndex = ref<number | null>(null);

  const removeUrlUploadInstance = (index: number) => {
    if (urlUploadInstances.value.length > 1 && urlUploadInstances.value[index].isValid) {
      pendingRemoveIndex.value = index;
      openRemoveConfirmation();
    } else {
      removeInstance(index);
    }
  };

  const { open: openModeChangeConfirmation, close: closeModeChangeConfirmation } = useModal({
    component: ConfirmCloseModal,
    attrs: {
      // @ts-ignore
      clickToClose: false,
      escToClose: false,
      title: 'Tem certeza que deseja alterar o modo de envio?',
      description:
        'Alterar o modo cancela todas as informações preenchidas. A ação não pode ser desfeita.',
      onClose() {
        closeModeChangeConfirmation();
        cancelModeChange();
        onCancelModeChange?.();
      },
      onConfirm() {
        closeModeChangeConfirmation();
        if (confirmModeChange()) {
          cleanState();
          handleAllFilesDeleted();

          resetInstances();
          isUrlUploadValid.value = false;
          urlUploadDataStore.clearAll();
        }
      },
    },
  });

  const handleModeChange = (newMode: UploadMode) => {
    if (shouldConfirmModeChange(newMode, hasFiles.value, hasValidUrls.value)) {
      setPendingMode(newMode);
      openModeChangeConfirmation();
    } else {
      setInitialMode(newMode);
    }
  };

  const collectUrlData = (): Array<{
    url: string;
    name: string;
    changelog?: string;
    managerId?: number;
  }> => {
    return [];
  };

  return {
    uploadMode,
    shouldConfirmModeChange,
    setInitialMode,
    setPendingMode,
    confirmModeChange,
    cancelModeChange,
    handleModeChange,

    urlUploadInstances,
    addUrlUploadInstance,
    removeUrlUploadInstance,
    handleUrlValidityChange,
    resetInstances,
    areAllUrlsValid,
    hasValidUrls,
    collectUrlData,

    pendingRemoveIndex,

    openModeChangeConfirmation,
    closeModeChangeConfirmation,
  };
}
